import { createDeviceMdmCommand, createGroupMdmCommand } from "@dashboard/devices/api";
import apiClient from "@dashboard/files/api/apiClient";
import { FileInfo } from "@files/types";

import { GroupApkTargets } from "./groupApkStatus";

export type InstallResult = {
    /** Devices the install actually reached, never the size of the group. */
    reached: number;
    failed: number;
    /** False when the devices were addressed individually, whose commands do not show in the group Commands tab. */
    usedGroupCommand: boolean;
};

// Browsers and the API gateway both choke on a few hundred simultaneous POSTs from one tab.
const deviceCommandConcurrency = 8;

const chunk = <T>(items: readonly T[], size: number): T[][] => {
    const batches: T[][] = [];
    for (let index = 0; index < items.length; index += size) {
        batches.push(items.slice(index, index + size));
    }
    return batches;
};

// Promise.allSettled, but one batch at a time — the next chunk starts only after the previous
// batch settles, which is what keeps in-flight POSTs at `size`.
const allSettledInChunks = async <T>(
    items: readonly T[],
    size: number,
    run: (item: T) => Promise<unknown>,
): Promise<PromiseSettledResult<unknown>[]> =>
    chunk(items, size).reduce<Promise<PromiseSettledResult<unknown>[]>>(
        async (previous, batch) => [...(await previous), ...(await Promise.allSettled(batch.map(run)))],
        Promise.resolve([]),
    );

export const installFileOnGroupDevices = async (args: {
    file: FileInfo;
    groupId: number;
    targets: GroupApkTargets;
}): Promise<InstallResult> => {
    if (!args.targets.eligible.length) {
        return { reached: 0, failed: 0, usedGroupCommand: false };
    }

    const url = await apiClient.getPresignedDownloadUrl(args.file.id);
    const json = {
        command: "installPackage",
        caption: "Install package",
        source: "url",
        url: url,
        package: args.file.metadata?.file?.apk?.manifest?.package,
        version: args.file.metadata?.file?.apk?.manifest?.versioncode,
    };

    // The group endpoint reaches every device whose license sits in the group and accepts no device
    // filter, so it is only safe when nothing would be downgraded. Otherwise address the eligible
    // devices individually, which is the only way to leave the newer ones alone.
    if (args.targets.eligible.length === args.targets.totalDevices) {
        const list = await createGroupMdmCommand({ groupId: args.groupId, json });
        return { reached: list.length, failed: 0, usedGroupCommand: true };
    }

    const results = await allSettledInChunks(args.targets.eligible, deviceCommandConcurrency, (device) =>
        createDeviceMdmCommand({ deviceId: device.id, json }),
    );

    return {
        reached: results.filter((result) => result.status === "fulfilled").length,
        failed: results.filter((result) => result.status === "rejected").length,
        usedGroupCommand: false,
    };
};
